// ---------------------------------------------------------------------------
// CaseStudy Renderer (editor key: CaseStudy)
// ---------------------------------------------------------------------------
// Renders a single case-study card with the company name, a headline
// metric badge, and the classic Challenge / Solution / Result narrative
// triad. All fields are display text — they pass through the `text()`
// escaper so untrusted input cannot break out of the surrounding markup.
// The block returns an HTML comment when nothing meaningful is filled in,
// matching the pattern used by every other PB-* renderer.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { text } from "../escape.js";

interface CaseStudyProps {
  company?: string;
  metric?: string;
  challenge?: string;
  solution?: string;
  result?: string;
}

interface NarrativeSection {
  label: string;
  body: string;
  modifier: string;
}

function renderNarrativeSection(section: NarrativeSection): string {
  if (!section.body) return "";
  return `    <div class="ease-case-study__section ease-case-study__section--${section.modifier}">
      <h4 class="ease-case-study__section-label">${text(section.label)}</h4>
      <p class="ease-case-study__section-body">${text(section.body)}</p>
    </div>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as CaseStudyProps;

  const company = typeof p.company === "string" ? p.company.trim() : "";
  const metric = typeof p.metric === "string" ? p.metric.trim() : "";
  const challenge = typeof p.challenge === "string" ? p.challenge.trim() : "";
  const solution = typeof p.solution === "string" ? p.solution.trim() : "";
  const result = typeof p.result === "string" ? p.result.trim() : "";

  if (!company && !metric && !challenge && !solution && !result) {
    return "<!-- case-study missing content -->";
  }

  const sections: NarrativeSection[] = [
    { label: "Challenge", body: challenge, modifier: "challenge" },
    { label: "Solution", body: solution, modifier: "solution" },
    { label: "Result", body: result, modifier: "result" },
  ];

  const headerHtml = company || metric
    ? `  <header class="ease-case-study__header">
    ${company ? `<h3 class="ease-case-study__company">${text(company)}</h3>` : ""}
    ${metric ? `<span class="ease-case-study__metric">${text(metric)}</span>` : ""}
  </header>`
    : "";

  const sectionsHtml = sections
    .map(renderNarrativeSection)
    .filter((html) => html.length > 0)
    .join("\n");

  return `<section class="ease-section ease-case-study">
  <article class="ease-case-study__card">
${headerHtml}
    <div class="ease-case-study__body">
${sectionsHtml}
    </div>
  </article>
</section>`;
}

registerRenderer("case-study", { render });
